import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import api from '@control-front-end/common/sagas/api';
import {
  ACTOR_ACCESS,
  ADD_ACTOR,
  CREATE_ACTOR,
  CREATE_ACTOR_LIST,
  GET_ACTOR,
  GET_ACTOR_TPL,
  GET_ACTORS,
  GET_ACTORS_BALANCE,
  GET_DEFAULT_ACTOR_TPL,
  GET_ACTOR_BY_IDS,
  GET_LINKED_ACTORS,
  UPDATE_ACTOR_VIEW,
  REMOVE_ACTOR,
  REMOVE_ACTOR_LIST,
  SEND_ACTORS_DATA,
  UPDATE_ACTOR,
  UPDATE_ACTOR_LIST,
  SET_ACTOR_MULTIPLICATOR,
  STARRED_ACTOR,
} from '@control-front-end/common/constants/graphActors';
import { RequestStatus } from 'constants';
import AppUtils from '@control-front-end/utils/utils';
import {
  sendReducerMsg,
  makeGraphModels,
  getAllGraphEls,
  getActiveLayer,
  getActorByGraphId,
  setActorsBalances,
  collapseNodesToMore,
  groupActorsByTpl,
  excludeAccessDenied,
  updateActorProp,
  updateActorOnGraph,
  extendModelWithAvatars,
} from './graphHelpers';
import { updateActorView } from '../actorView';
import { manageLayerElements } from '../../routes/ActorsGraph/sagas/layers/layerElements';
import { manageAccessRules } from '../accessRules';

/**
 * Обновить элемент списка
 */
function* updateActorInList({ actorId, field, value }) {
  const { list } = yield select((state) => state.actorsList);
  const copyList = list.slice();
  const index = copyList.findIndex((i) => i.id === actorId);
  if (index !== -1) {
    const copyActor = { ...copyList[index] };
    copyActor[field] = value;
    copyList.splice(index, 1, copyActor);
    return copyList;
  }
  return copyList;
}

/**
 * Получить шаблон актора по умолчанию
 */
function* getDefaultActorTpl({ callback }) {
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/forms/default/${accId}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({
    type: GET_DEFAULT_ACTOR_TPL.SUCCESS,
    payload: data.data,
  });
  if (callback) callback(data.data);
}

/**
 * Получить шаблон актора
 */
function* getActorTpl({ payload, callback }) {
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/forms/${payload}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({
    type: GET_ACTOR_TPL.SUCCESS,
    payload: data.data,
  });
  if (callback) callback(data.data);
}

/**
 * Обработка клика по кнопке на форме актора
 */
function* sendActorsData({ payload, callback }) {
  const { actorId, buttonId, data: body } = payload;
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/actors_graph/send/${actorId}/${buttonId}`,
    body,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({ type: SEND_ACTORS_DATA.SUCCESS });
  if (callback) callback(data.data);
}

/**
 * Получить баланс акторов
 */
function* getActorsBalance({ payload, callback }) {
  const {
    layerId,
    currencyId,
    nameId,
    actorIds,
    accountType = 'fact',
  } = payload;
  const graphL = yield select((state) => state.graphLayers);
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const layer = graphL.list.find(
    (i) => i.id === layerId || i.layerId === layerId
  );
  if (!layer) return;
  const copyNodes = layer.nodes ? structuredClone(layer.nodes) : [];
  const nodeIds =
    actorIds ||
    copyNodes.filter((i) => i.data.type === 'node').map((a) => a.id);
  if (!nodeIds.length) return;
  if (currencyId && nameId) {
    const { result, data } = yield call(api, {
      method: 'post',
      url: `/actors_graph/balance/${accId}`,
      queryParams: { currencyId, nameId, accountType },
      body: nodeIds,
    });
    if (result !== RequestStatus.SUCCESS) return;
    setActorsBalances({ balances: data.data, nodes: copyNodes });
  } else {
    copyNodes.forEach((node) => {
      if (node.data.type === 'node') {
        delete node.data.balanceVector;
        node.data.balance = null;
        node.status = 'updated';
      }
    });
  }
  yield sendReducerMsg({
    layerId,
    type: GET_ACTORS_BALANCE.SUCCESS,
    payload: { nodes: copyNodes },
  });
  if (callback) callback();
}

/**
 * Изменить права доступа на слой
 */
function* manageActorAccess({ payload }) {
  const { id, access } = payload;
  const { list } = yield select((state) => state.actorsList);
  const copyList = list.slice();
  const actor = copyList.find((i) => i.id === id);
  if (!actor) return;
  actor.access = access;
  yield put({
    type: ACTOR_ACCESS.SUCCESS,
    payload: { list: copyList },
  });
}

/**
 * Получить актор и связанные с ним акторы
 */
export function* getActors({ payload, typeLayer }) {
  const { actorId, edgeType } = payload;
  const edgeTypes = yield select((state) => state.edgeTypes);
  const edgeTypeM = edgeTypes.find((i) => i.name === edgeType) || {};
  const edgeTypeId = payload.edgeTypeId || edgeTypeM.id;
  const reqAId = actorId.split('_')[0];
  let url;
  switch (typeLayer) {
    case 'trees':
      url = `/graph/tree/${reqAId}`;
      break;
    case 'actors':
      url = `/graph/linked_actors/${reqAId}`;
      break;
    default:
      url = `/graph/linked_actors/${reqAId}`;
  }
  const { result, data } = yield call(api, {
    method: 'get',
    url,
    queryParams: { edgeTypeId },
  });
  if (result !== RequestStatus.SUCCESS) return;
  let availableNodes;
  let availableEdges;
  // только для дерева счетов оставляем узлы без доступа
  const isTreeLayer = typeLayer === 'trees';
  if (!isTreeLayer) {
    const graphEls = excludeAccessDenied(data.data);
    availableNodes = graphEls.nodes;
    availableEdges = graphEls.edges;
  }
  const { nodes, edges } = yield call(makeGraphModels, {
    nodes: isTreeLayer ? data.data.nodes : availableNodes,
    edges: isTreeLayer ? data.data.edges : availableEdges,
    isTree: typeLayer !== 'graph',
  });
  let groupEdges = edges;
  let hiddenEdges = [];
  if (typeLayer === 'actors') {
    const groupRes = yield groupActorsByTpl({
      rootActorId: reqAId,
      nodes,
      edges,
      edgeTypeId,
      typeLayer,
    });
    groupEdges = groupRes.groupEdges;
    hiddenEdges = groupRes.hiddenEdges;
    yield collapseNodesToMore({
      rootActorId: reqAId,
      nodes,
      edges: groupEdges,
      edgeTypeId,
    });
  }
  const prevNodes = typeLayer ? [] : yield call(getAllGraphEls, 'nodes');
  const prevEdges = typeLayer ? [] : yield call(getAllGraphEls, 'edges');
  const newNodes = AppUtils.uniqueObjArray(prevNodes.concat(nodes), 'id');
  const newEdges = AppUtils.uniqueObjArray(prevEdges.concat(groupEdges), 'id');
  if (!isTreeLayer) {
    const actor = newNodes.find((i) => i.data.actorId === actorId);
    actor.data.linkedActorsLoaded = true;
  }
  const payloadRes = {
    key: AppUtils.createRid(),
    nodes: newNodes,
    edges: newEdges,
    hiddenEdges,
    rootTreeId: data.data.rootTreeId,
    treeEdgeTypeInfo: data.data.treeEdgeTypeInfo,
    activeElement: actorId,
    activeType: 'node',
  };
  if (!typeLayer) {
    yield sendReducerMsg({
      type: GET_ACTORS.SUCCESS,
      payload: payloadRes,
    });
  }
  return payloadRes;
}

/**
 * Получить актор
 */
function* getActor({ payload, callback, errorCallback }) {
  const {
    id,
    attachments = false,
    actorView = false,
    reactionsStats,
  } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_graph/${id}`,
    queryParams: { attachments, reactionsStats },
    handleErrCodes: errorCallback ? [403] : [],
  });
  if (data.statusCode === 403 && errorCallback) {
    errorCallback();
  }
  if (result !== RequestStatus.SUCCESS)
    return { error: true, title: 'Access Denied' };
  // Если обновился шаблон, то подставляем его новый id
  if (data.data.currentTemplateId !== data.data.actorTemplateId) {
    data.data.formId = data.data.currentTemplateId;
  }
  yield call(extendModelWithAvatars, data.data);
  if (data.data.attachments) {
    for (const i of data.data.attachments) {
      i.filePath = yield call(AppUtils.makeAppUrl, `/download/${i.fileName}`);
    }
  }
  data.data.actorId = data.data.id;
  if (actorView) {
    yield put({
      type: UPDATE_ACTOR_VIEW.SUCCESS,
      payload: data.data,
    });
  }
  const { list } = yield select((state) => state.actorsList);
  const copyList = list.slice();
  const index = copyList.findIndex((i) => i.id === payload);
  if (index !== -1) {
    const newActor = data.data;
    copyList.splice(index, 1, newActor);
    yield put({
      type: UPDATE_ACTOR_LIST.SUCCESS,
      payload: {
        list: copyList,
      },
    });
  }
  if (callback) callback(data.data);
  return data.data;
}

/**
 * Получить связанные акторы
 */
function* getLinkedActors({ payload, callback }) {
  const {
    id,
    formId,
    exceptFormId,
    limit,
    offset,
    filterDenied = false,
    actorView = false,
  } = payload;
  const edgeType = 'hierarchy';
  const edgeTypes = yield select((state) => state.edgeTypes);
  const edgeTypeH = edgeTypes.find((i) => i.name === edgeType);
  const queryParams = {
    edgeTypeId: edgeTypeH.id,
    formId,
    exceptFormId,
    limit,
    offset,
  };
  if (filterDenied) queryParams.filterDenied = filterDenied;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/graph/linked/${id}`,
    queryParams,
  });
  if (result !== RequestStatus.SUCCESS) return;
  for (const obj of data.data.list) {
    yield call(extendModelWithAvatars, obj);
  }
  if (actorView) {
    yield call(updateActorView, {
      payload: { actorData: { id, actors: data.data.list } },
    });
  }
  yield put({
    type: GET_LINKED_ACTORS.SUCCESS,
    payload: { list: data.data },
  });
  if (callback) callback(data.data);
}

/**
 * Получить список акторов по id
 */
function* getActorsByIds({ payload, callback }) {
  const reqs = [];
  for (const id of payload) {
    reqs.push(
      call(getActor, {
        payload: { id },
        handleErrCodes: [403],
        errorCallback: () => {},
      })
    );
  }
  const resp = yield all(reqs);
  const actors = [];
  for (const { id, title } of resp) {
    actors.push({ id, title });
  }
  yield put({
    type: GET_ACTOR_BY_IDS.SUCCESS,
    payload: actors,
  });
  callback(actors);
}

/**
 * Создать актор
 */
export function* createActor({ payload, callback }) {
  const {
    title,
    description,
    picture,
    pictureObject,
    areaPicture,
    formData,
    position,
    cardActorId,
    appId,
    appSettings,
    access,
    extraData = {},
  } = payload;
  let { formId } = payload;
  if (!formId) {
    const defaultActorTpl = yield select((state) => state.defaultActorTpl);
    formId = defaultActorTpl.id;
  }
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/actors_graph/actor/${formId}`,
    body: {
      title,
      description,
      picture,
      pictureObject,
      cardActorId,
      appId,
      appSettings,
      data: formData || {},
    },
  });
  if (result !== RequestStatus.SUCCESS) return;

  const newModel = { ...data.data, position, areaPicture, ...extraData };
  newModel.actorId = data.data.id;
  yield call(extendModelWithAvatars, newModel);
  if (access) {
    yield manageAccessRules({
      payload: {
        body: access,
        objType: 'actor',
        objId: newModel.id,
        recursive: false,
      },
    });
  }

  const activeLayer = yield getActiveLayer();
  if (activeLayer) {
    const isDefaultTitle = /^Actor\s[a-z0-9]{8}$/.test(newModel.title);
    const { addedNodes } = yield call(manageLayerElements, {
      payload: {
        layerId: activeLayer.id,
        activeElement: newModel.id,
        activeType: 'node',
        editableElement: isDefaultTitle ? newModel.id : null,
        body: [
          {
            action: 'create',
            data: {
              id: newModel.id,
              model: newModel,
              position,
              areaPicture,
              type: 'node',
            },
          },
        ],
        withReq: activeLayer.typeLayer === 'layers',
      },
    });
    newModel.laId = addedNodes[0].laId;
  } else {
    const { list, formId: activeFormId } = yield select(
      (state) => state.actorsList
    );
    const findEl = list.find((i) => i.id === newModel.id);
    if (!findEl && newModel.formId === activeFormId) {
      const newList = list.slice();
      newList.unshift(newModel);
      yield put({
        type: CREATE_ACTOR_LIST.SUCCESS,
        payload: {
          list: newList,
          activeActor: newModel.id,
        },
      });
    }
  }
  yield put({ type: CREATE_ACTOR.SUCCESS });
  if (callback) callback(newModel);
  return newModel;
}

/**
 * Добавить модель актора на граф
 */
export function* addActor({ payload, callback }) {
  const { actorModel, position, areaPicture, manageLayer } = payload;
  const activeLayer = yield getActiveLayer();
  const layerId = payload.layerId || activeLayer.id;
  const { nodesMap } = yield call(manageLayerElements, {
    payload: {
      layerId,
      body: [
        {
          action: 'create',
          data: {
            id: actorModel.id,
            model: {
              ...actorModel,
              position,
              areaPicture,
            },
            position,
            areaPicture,
            type: 'node',
          },
        },
      ],
      withReq: manageLayer,
    },
  });
  actorModel.laId = nodesMap[0].laId;
  yield put({ type: ADD_ACTOR.SUCCESS });
  if (callback) callback(actorModel);
}

/**
 * Удалить актор
 */
function* removeActor({ payload, callback }) {
  const { id, manageLayer } = payload;
  const nodeModel = yield getActorByGraphId(id, 'nodes');
  const activeLayer = yield getActiveLayer();
  const actorId = nodeModel && nodeModel.actorId ? nodeModel.actorId : id;
  if (!manageLayer) {
    const { result } = yield call(api, {
      method: 'delete',
      url: `/actors_graph/${actorId}`,
    });
    if (result !== RequestStatus.SUCCESS) return;
  }
  if (activeLayer && nodeModel) {
    yield call(manageLayerElements, {
      payload: {
        layerId: activeLayer.id,
        activeElement: null,
        activeType: null,
        editableElement: null,
        body: [
          {
            action: 'delete',
            data: {
              id: nodeModel.actorId,
              model: nodeModel,
              type: 'node',
              withDuplicates: !manageLayer,
            },
          },
        ],
        withReq: manageLayer,
      },
    });
  }
  const { list, activeActor } = yield select((state) => state.actorsList);
  const newList = list.filter((i) => i.id !== id);
  const newActiveActor = activeActor !== id ? activeActor : null;
  yield put({
    type: REMOVE_ACTOR_LIST.SUCCESS,
    payload: {
      list: newList,
      activeActor: newActiveActor,
    },
  });
  if (callback) callback();
}

/**
 * Обновить актор
 */
export function* updateActor({ payload, callback }) {
  const {
    title,
    description,
    picture,
    pictureObject,
    id,
    formId,
    ref,
    formData,
    appId,
    appSettings,
    cardActorId,
  } = payload;
  const newActorData = {
    title,
    description,
    picture,
    pictureObject,
    ref,
    appId,
    appSettings,
    cardActorId,
  };
  let nModel = yield getActorByGraphId(id, 'nodes');
  if (!nModel) nModel = { actorId: id };
  if (formData) newActorData.data = formData;
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/actors_graph/actor/${formId}/${nModel.actorId || nModel.id}`,
    body: newActorData,
  });
  if (result !== RequestStatus.SUCCESS) {
    if (callback) callback();
    return;
  }
  const newActor = { ...data.data };
  yield call(updateActorView, { payload: { actorData: newActor } });
  const { list } = yield select((state) => state.actorsList);
  const copyList = structuredClone(list);
  const index = copyList.findIndex((i) => i.id === id);
  newActor.laId = nModel.laId;
  newActor.actorId = nModel.actorId;
  newActor.position = nModel.position;
  yield call(extendModelWithAvatars, newActor);
  const { nodes } = yield call(updateActorOnGraph, { actorModel: newActor });
  yield sendReducerMsg({
    type: UPDATE_ACTOR.SUCCESS,
    payload: { nodes, editableElement: null },
  });
  if (callback) callback(data.data);
  if (index === -1) return;
  copyList.splice(index, 1, newActor);
  yield put({
    type: UPDATE_ACTOR_LIST.SUCCESS,
    payload: {
      list: copyList,
    },
  });
}

/**
 * Изменить мультипликатор акторов
 */
function* setActorMultiplicator({ payload, callback }) {
  const { actorId, multiplicator } = payload;
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/actors_graph/multiplicator/${actorId}`,
    body: { multiplicator },
  });
  if (result !== RequestStatus.SUCCESS) return;
  const graphLayers = yield select((state) => state.graphLayers);
  const { active } = graphLayers;
  yield updateActorProp({
    type: SET_ACTOR_MULTIPLICATOR.SUCCESS,
    layerId: active,
    actorId,
    propId: 'multiplicator',
    value: multiplicator,
  });
  const copyList = yield updateActorInList({
    actorId,
    field: 'multiplicator',
    value: multiplicator,
  });
  yield call(updateActorView, {
    payload: { actorData: { id: actorId, multiplicator } },
  });
  yield put({
    type: UPDATE_ACTOR_LIST.SUCCESS,
    payload: {
      list: copyList,
    },
  });
  if (callback) callback(data.data);
}

/**
 * Изменить флаг starred
 */
function* starredActor({ payload }) {
  const { actorId, starred } = payload;
  const { result } = yield call(api, {
    method: 'post',
    url: `/actors_graph/starred/${actorId}/${starred}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield call(updateActorView, {
    payload: { actorData: { id: actorId, starred } },
  });
  const copyList = yield updateActorInList({
    actorId,
    field: 'starred',
    value: starred,
  });
  yield put({
    type: UPDATE_ACTOR_LIST.SUCCESS,
    payload: {
      list: copyList,
    },
  });
}

function* graphNodes() {
  yield takeEvery(GET_ACTORS.REQUEST, getActors);
  yield takeEvery(GET_ACTOR.REQUEST, getActor);
  yield takeEvery(GET_ACTOR_BY_IDS.REQUEST, getActorsByIds);
  yield takeEvery(GET_LINKED_ACTORS.REQUEST, getLinkedActors);
  yield takeEvery(CREATE_ACTOR.REQUEST, createActor);
  yield takeEvery(UPDATE_ACTOR.REQUEST, updateActor);
  yield takeEvery(REMOVE_ACTOR.REQUEST, removeActor);
  yield takeEvery(ADD_ACTOR.REQUEST, addActor);
  yield takeEvery(SEND_ACTORS_DATA.REQUEST, sendActorsData);
  yield takeEvery(ACTOR_ACCESS.REQUEST, manageActorAccess);
  yield takeEvery(GET_ACTORS_BALANCE.REQUEST, getActorsBalance);
  yield takeEvery(GET_DEFAULT_ACTOR_TPL.REQUEST, getDefaultActorTpl);
  yield takeEvery(GET_ACTOR_TPL.REQUEST, getActorTpl);
  yield takeEvery(SET_ACTOR_MULTIPLICATOR.REQUEST, setActorMultiplicator);
  yield takeEvery(STARRED_ACTOR.REQUEST, starredActor);
}

export default graphNodes;
